import React from "react"
import { Control, FieldPath, FieldValues } from "react-hook-form"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormErrorMessage,
  FormDescription,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

interface FormInputFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  placeholder?: string
  type?: string
  disabled?: boolean
  description?: string
  className?: string
  labelClassName?: string
  rightElement?: React.ReactNode
}

/**
 * A standardized form input field that combines Label, Input, and Error Message.
 * Inherits all Meatbid Industrial Design standards.
 */
export function FormInputField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  type = "text",
  disabled,
  description,
  className,
  labelClassName,
  rightElement,
}: FormInputFieldProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && <FormLabel className={labelClassName}>{label}</FormLabel>}
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                // Handle null/undefined values
                value={field.value ?? ""}
                onChange={(e) => {
                  const val = type === "number" ? parseFloat(e.target.value) || 0 : e.target.value
                  field.onChange(val)
                }}
              />
              {rightElement && (
                <div className="absolute right-0 top-0 flex h-full items-center">
                  {rightElement}
                </div>
              )}
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormErrorMessage />
        </FormItem>
      )}
    />
  )
}

